import { Switch } from '@advanza/advanza_generic'
import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button } from '@advanza/ui'
import Card from 'components/Card'
import { useToast } from 'components/misc/Toaster'
import { CSSProperties, useEffect, useState } from 'react'

interface PartnerPushSubscription {
    partner_push_subscription_id: number
    name: string
    target_url: string
    service_provider_id: number | null
    events: string[]
    enabled: boolean
    mail_lead: boolean
    provider?: {
        service_provider_id: number
        business_name: string
    } | null
}

interface PartnerPushDelivery {
    partner_push_delivery_id: number
    request_id: number
    match_id: number | null
    service_provider_id: number | null
    event: string
    status: string
    http_status: number | null
    attempt_count: number
    error: string | null
    created: string
}

interface SubscriptionForm {
    name: string
    target_url: string
    providerIdText: string
    enabled: boolean
    mail_lead: boolean
}

interface SaveResponse {
    keyMap?: Record<string, number>
    secrets?: Record<string, string>
}

interface SubscriptionFieldsProps {
    value: SubscriptionForm
    onChange: (next: SubscriptionForm) => void
}

const emptyForm: SubscriptionForm = {
    name: '',
    target_url: '',
    providerIdText: '',
    enabled: true,
    mail_lead: true,
}

const CREATE_KEY = 'tmp'

const cellStyle: CSSProperties = { padding: '8px', textAlign: 'left' }

const fieldRow: CSSProperties = {
    display: 'block',
    width: '100%',
    marginBottom: 0,
}

const parseProviderId = (text: string): number => {
    const trimmed = text.trim()
    const id = Number(trimmed)
    if (trimmed === '' || !Number.isInteger(id) || id <= 0) {
        throw new Error('Invalid provider ID')
    }

    return id
}

const isValidProviderIdText = (text: string): boolean => {
    try {
        parseProviderId(text)
        return true
    } catch {
        return false
    }
}

const toForm = (item: PartnerPushSubscription): SubscriptionForm => ({
    name: item.name,
    target_url: item.target_url,
    providerIdText: item.service_provider_id === null ? '' : String(item.service_provider_id),
    enabled: item.enabled,
    mail_lead: item.mail_lead,
})

const urlHint = (url: string): string | null => {
    const trimmed = url.trim()
    if (trimmed === '' || trimmed.startsWith('https://')) {
        return null
    }

    return 'URL must start with https://'
}

const SubscriptionFields = ({ value, onChange }: SubscriptionFieldsProps) => {
    const invalidUrl = urlHint(value.target_url)

    return (
        <div className="flex w-full flex-col gap-4">
            <div style={fieldRow}>
                <InputField
                    legend
                    classNames="w-full"
                    style={{ width: '100%' }}
                    placeholder="Name"
                    value={value.name}
                    onChange={(e) => onChange({ ...value, name: e.target.value })}
                />
            </div>
            <div style={fieldRow}>
                <InputField
                    legend
                    classNames="w-full"
                    style={{ width: '100%' }}
                    placeholder="HTTPS URL"
                    value={value.target_url}
                    onChange={(e) => onChange({ ...value, target_url: e.target.value })}
                />
                {invalidUrl !== null && (
                    <div style={{ color: '#b42318', fontSize: '12px', marginTop: '4px' }}>
                        {invalidUrl}
                    </div>
                )}
            </div>
            <div style={fieldRow}>
                <InputField
                    legend
                    classNames="w-full"
                    style={{ width: '100%' }}
                    placeholder="Provider ID"
                    value={value.providerIdText}
                    onChange={(e) => onChange({ ...value, providerIdText: e.target.value })}
                />
                <div style={{ color: '#666666', fontSize: '12px', marginTop: '4px' }}>
                    The service provider ID this connection delivers for. A franchise main account
                    also receives the leads of its sub-accounts.
                </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Switch
                    checked={value.enabled}
                    onChange={() => onChange({ ...value, enabled: !value.enabled })}
                />
                {value.enabled ? 'Enabled' : 'Disabled'}
            </label>
            <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch
                        checked={value.mail_lead}
                        onChange={() => onChange({ ...value, mail_lead: !value.mail_lead })}
                    />
                    {value.mail_lead ? 'Lead mail on' : 'Lead mail off'}
                </label>
                <div style={{ color: '#666666', fontSize: '12px', marginTop: '4px' }}>
                    Whether the provider still receives the normal lead mail on top of the webhook.
                    Reminders and nudges are suppressed either way while the subscription is
                    enabled.
                </div>
            </div>
        </div>
    )
}

const PartnerPushList = () => {
    const { addToast } = useToast()
    const [items, setItems] = useState<PartnerPushSubscription[]>([])
    const [loading, setLoading] = useState(true)
    const [newItem, setNewItem] = useState<SubscriptionForm>(emptyForm)
    const [isSaving, setIsSaving] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editFields, setEditFields] = useState<SubscriptionForm | null>(null)
    const [deliveries, setDeliveries] = useState<PartnerPushDelivery[]>([])
    const [revealedSecret, setRevealedSecret] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [togglingId, setTogglingId] = useState<number | null>(null)

    const fetchItems = async (silent = false): Promise<PartnerPushSubscription[]> => {
        if (!silent) {
            setLoading(true)
        }
        try {
            const response = await call('office/partner-push-subscriptions/get-list')
            const next = response.items ?? []
            setItems(next)
            return next
        } catch {
            addToast({ msg: 'Failed to load partner push subscriptions.', red: true })
            return []
        } finally {
            if (!silent) {
                setLoading(false)
            }
        }
    }

    const fetchDeliveries = async (subscriptionId: number) => {
        try {
            const response = await call('office/partner-push-deliveries/get-list', {
                query: { subscriptionId, pageSize: 20 },
            })
            setDeliveries(response.items ?? [])
        } catch {
            addToast({ msg: 'Failed to load deliveries.', red: true })
        }
    }

    useEffect(() => {
        void fetchItems()
    }, [])

    const startEdit = (item: PartnerPushSubscription, keepSecret = false) => {
        setEditingId(item.partner_push_subscription_id)
        setEditFields(toForm(item))
        if (!keepSecret) {
            setRevealedSecret(null)
            setCopied(false)
        }
        void fetchDeliveries(item.partner_push_subscription_id)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const cancelEdit = () => {
        setEditingId(null)
        setEditFields(null)
        setDeliveries([])
    }

    const copySecret = async (secret: string) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(secret)
            } else {
                const textArea = document.createElement('textarea')
                textArea.value = secret
                textArea.setAttribute('readonly', '')
                textArea.style.position = 'fixed'
                textArea.style.left = '-9999px'
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand('copy')
                document.body.removeChild(textArea)
            }
            setCopied(true)
            addToast({ msg: 'Secret copied.', green: true })
            window.setTimeout(() => setCopied(false), 2000)
        } catch {
            addToast({ msg: 'Could not copy the secret. Select it and copy manually.', red: true })
        }
    }

    const saveNew = async () => {
        let providerId: number
        try {
            providerId = parseProviderId(newItem.providerIdText)
        } catch {
            addToast({ msg: 'Provider ID must be a positive integer.', red: true })
            return
        }

        setIsSaving(true)
        try {
            const response: SaveResponse = await call('office/partner-push-subscriptions/save', {
                method: 'POST',
                json: [
                    {
                        partner_push_subscription_id: CREATE_KEY,
                        name: newItem.name,
                        target_url: newItem.target_url,
                        service_provider_id: providerId,
                        events: ['request.matched'],
                        enabled: newItem.enabled,
                        mail_lead: newItem.mail_lead,
                    },
                ],
            })
            const newId = response.keyMap?.[CREATE_KEY]
            const secret = response.secrets?.[CREATE_KEY] ?? null
            setNewItem(emptyForm)
            const nextItems = await fetchItems(true)
            const created = nextItems.find((item) => item.partner_push_subscription_id === newId)
            if (created !== undefined) {
                startEdit(created, true)
            }
            setRevealedSecret(secret)
            setCopied(false)
            addToast({
                msg: secret === null ? 'Created.' : 'Created. Copy the secret now.',
                green: true,
            })
        } catch {
            addToast({
                msg: 'Failed to create subscription. Check the HTTPS URL and that the provider ID exists.',
                red: true,
            })
        } finally {
            setIsSaving(false)
        }
    }

    const saveEdit = async () => {
        if (editingId === null || editFields === null) {
            return
        }

        let providerId: number
        try {
            providerId = parseProviderId(editFields.providerIdText)
        } catch {
            addToast({ msg: 'Provider ID must be a positive integer.', red: true })
            return
        }

        setIsSaving(true)
        try {
            await call('office/partner-push-subscriptions/save', {
                method: 'POST',
                json: [
                    {
                        partner_push_subscription_id: editingId,
                        name: editFields.name,
                        target_url: editFields.target_url,
                        service_provider_id: providerId,
                        enabled: editFields.enabled,
                        mail_lead: editFields.mail_lead,
                    },
                ],
            })
            addToast({ msg: 'Saved.', green: true })
            await fetchItems(true)
        } catch {
            addToast({ msg: 'Failed to save subscription.', red: true })
        } finally {
            setIsSaving(false)
        }
    }

    const rotateSecret = async (id: number) => {
        if (!window.confirm('Rotate the shared secret? The previous secret stops working.')) {
            return
        }
        try {
            const response = await call(`office/partner-push-subscriptions/rotate-secret/${id}`, {
                method: 'POST',
            })
            setRevealedSecret(response.secret ?? null)
            setCopied(false)
            addToast({ msg: 'Copy the new secret now.', green: true })
        } catch {
            addToast({ msg: 'Failed to rotate secret.', red: true })
        }
    }

    const retryDelivery = async (deliveryId: number) => {
        try {
            await call(`office/partner-push-deliveries/retry/${deliveryId}`, {
                method: 'POST',
            })
            addToast({ msg: 'Delivery re-queued.', green: true })
            if (editingId !== null) {
                void fetchDeliveries(editingId)
            }
        } catch {
            addToast({ msg: 'Failed to retry delivery.', red: true })
        }
    }

    const toggleEnabled = async (item: PartnerPushSubscription, enabled: boolean) => {
        if (togglingId !== null) {
            return
        }

        setTogglingId(item.partner_push_subscription_id)
        setItems((current) =>
            current.map((row) =>
                row.partner_push_subscription_id === item.partner_push_subscription_id
                    ? { ...row, enabled }
                    : row,
            ),
        )
        if (editingId === item.partner_push_subscription_id && editFields !== null) {
            setEditFields({ ...editFields, enabled })
        }

        try {
            await call('office/partner-push-subscriptions/save', {
                method: 'POST',
                json: [
                    {
                        partner_push_subscription_id: item.partner_push_subscription_id,
                        enabled,
                    },
                ],
            })
        } catch {
            setItems((current) =>
                current.map((row) =>
                    row.partner_push_subscription_id === item.partner_push_subscription_id
                        ? { ...row, enabled: item.enabled }
                        : row,
                ),
            )
            if (editingId === item.partner_push_subscription_id && editFields !== null) {
                setEditFields({ ...editFields, enabled: item.enabled })
            }
            addToast({ msg: 'Failed to update status.', red: true })
        } finally {
            setTogglingId(null)
        }
    }

    const canAdd =
        !isSaving &&
        newItem.name.trim() !== '' &&
        newItem.target_url.startsWith('https://') &&
        isValidProviderIdText(newItem.providerIdText)
    const canSaveEdit =
        !isSaving &&
        editFields !== null &&
        editFields.name.trim() !== '' &&
        editFields.target_url.startsWith('https://') &&
        isValidProviderIdText(editFields.providerIdText)

    if (loading) {
        return <div>Loading...</div>
    }

    const isEditing = editingId !== null && editFields !== null

    return (
        <div className="flex flex-col gap-6">
            <Card header={isEditing ? `Edit #${editingId}` : 'Add subscription'} icon="add_link">
                <div className="mt-2 flex w-full flex-col gap-4">
                    {isEditing ? (
                        <SubscriptionFields value={editFields} onChange={setEditFields} />
                    ) : (
                        <SubscriptionFields value={newItem} onChange={setNewItem} />
                    )}
                    <div className="flex flex-wrap gap-2">
                        {isEditing ? (
                            <>
                                <Button disabled={!canSaveEdit} onClick={() => void saveEdit()}>
                                    Save
                                </Button>
                                <Button name="link" onClick={cancelEdit}>
                                    Cancel
                                </Button>
                                <Button name="text" onClick={() => void rotateSecret(editingId)}>
                                    Rotate secret
                                </Button>
                            </>
                        ) : (
                            <Button disabled={!canAdd} onClick={() => void saveNew()}>
                                Add
                            </Button>
                        )}
                    </div>
                    {revealedSecret !== null && (
                        <p className="m-0 break-all">
                            Secret (copy now): <code>{revealedSecret}</code>{' '}
                            <Button
                                name="text"
                                type="button"
                                onClick={(e: { preventDefault: () => void }) => {
                                    e.preventDefault()
                                    void copySecret(revealedSecret)
                                }}>
                                {copied ? 'Copied' : 'Copy'}
                            </Button>
                        </p>
                    )}
                </div>
            </Card>

            <Card header="Subscriptions">
                {items.length === 0 ? (
                    <p className="m-0">No subscriptions yet.</p>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #dddddd' }}>
                                <th style={cellStyle}>Name</th>
                                <th style={cellStyle}>Provider</th>
                                <th style={cellStyle}>Enabled</th>
                                <th style={cellStyle}>Lead mail</th>
                                <th style={cellStyle}>URL</th>
                                <th style={cellStyle}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => {
                                const selected = editingId === item.partner_push_subscription_id
                                return (
                                    <tr
                                        key={item.partner_push_subscription_id}
                                        style={{
                                            borderBottom: '1px solid #eeeeee',
                                            background: selected ? '#f5f5f5' : undefined,
                                            opacity: item.enabled ? 1 : 0.55,
                                        }}>
                                        <td style={cellStyle}>{item.name}</td>
                                        <td style={cellStyle}>
                                            {item.service_provider_id === null
                                                ? '—'
                                                : `#${item.service_provider_id}${
                                                      item.provider?.business_name
                                                          ? ` ${item.provider.business_name}`
                                                          : ''
                                                  }`}
                                        </td>
                                        <td style={cellStyle}>
                                            <Switch
                                                checked={item.enabled}
                                                disabled={
                                                    togglingId === item.partner_push_subscription_id
                                                }
                                                onChange={() =>
                                                    void toggleEnabled(item, !item.enabled)
                                                }
                                            />
                                        </td>
                                        <td style={cellStyle}>{item.mail_lead ? 'On' : 'Off'}</td>
                                        <td style={{ ...cellStyle, wordBreak: 'break-all' }}>
                                            {item.target_url}
                                        </td>
                                        <td style={cellStyle}>
                                            <Button name="text" onClick={() => startEdit(item)}>
                                                Edit
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </Card>

            {isEditing && (
                <Card header="Recent deliveries">
                    {deliveries.length === 0 ? (
                        <p className="m-0">No deliveries yet.</p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #dddddd' }}>
                                    <th style={cellStyle}>Request</th>
                                    <th style={cellStyle}>Provider</th>
                                    <th style={cellStyle}>Status</th>
                                    <th style={cellStyle}>HTTP</th>
                                    <th style={cellStyle}>Attempts</th>
                                    <th style={cellStyle}>Error</th>
                                    <th style={cellStyle}>Created</th>
                                    <th style={cellStyle}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.map((delivery) => (
                                    <tr
                                        key={delivery.partner_push_delivery_id}
                                        style={{ borderBottom: '1px solid #eeeeee' }}>
                                        <td style={cellStyle}>{delivery.request_id}</td>
                                        <td style={cellStyle}>
                                            {delivery.service_provider_id ?? ''}
                                        </td>
                                        <td style={cellStyle}>{delivery.status}</td>
                                        <td style={cellStyle}>{delivery.http_status ?? ''}</td>
                                        <td style={cellStyle}>{delivery.attempt_count}</td>
                                        <td style={cellStyle}>{delivery.error ?? ''}</td>
                                        <td style={cellStyle}>{delivery.created}</td>
                                        <td style={cellStyle}>
                                            {(delivery.status === 'pending' ||
                                                delivery.status === 'failed') && (
                                                <Button
                                                    name="text"
                                                    onClick={() =>
                                                        void retryDelivery(
                                                            delivery.partner_push_delivery_id,
                                                        )
                                                    }>
                                                    Retry
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Card>
            )}
        </div>
    )
}

export default PartnerPushList
